import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Loading } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { getCampaign } from '@vnetwork/margarita-core/attribution';
import { rememberServiceInterest } from '@/lib/lead-service-interest';
import AccessRemovedPage from '@/pages/auth/AccessRemovedPage';

/** Gate authenticated areas: wait for the session, else bounce to auth. */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isContactRevoked } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <Loading />;
  }

  // Valid token but the account maps to no live org contact (removed member):
  // show the "access removed" screen instead of mounting the console, so no
  // customer-scoped request fires and the user isn't bounced into a login loop.
  if (isAuthenticated && isContactRevoked) {
    return <AccessRemovedPage />;
  }

  if (!isAuthenticated) {
    // Remember which service the visitor was headed to so a later signup lead
    // carries it as the interested product (the generic register form only sends
    // an email, otherwise the lead's "Giải pháp" is blank).
    rememberServiceInterest(location.pathname);
    // Campaign visitors (e.g. utm_campaign=waf_free) are a sign-up funnel — send
    // them straight to register (which shows the campaign lead form) instead of
    // login. The register form still offers an "Already a member? Login" escape.
    const to = getCampaign() === 'waf_free' ? '/auth/register' : '/auth/login';
    // Preserve the full intended URL (path + query) so post-auth we can return
    // the user exactly where they were headed (e.g. a reconciliation deep link).
    return (
      <Navigate
        to={to}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <>{children}</>;
}
